import type { AdminUser } from "@storyline/shared";
import { useCallback, useEffect, useState } from "react";
import { listAdminUsers } from "@/src/users/services/admin-users-http.service";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

export const useAdminUsers = () => {
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [users, setUsers] = useState<AdminUser[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [hasMore, setHasMore] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	const loadPage = useCallback(
		async (cursor: number, append: boolean) => {
			setIsLoading(true);
			try {
				const data = await listAdminUsers({ query: debouncedSearch || undefined, limit: PAGE_SIZE, cursor });
				setUsers((previous) => (append ? [...previous, ...data.users] : data.users));
				setHasMore(data.users.length === PAGE_SIZE);
				setHasError(false);
			} catch {
				setHasError(true);
			} finally {
				setIsLoading(false);
			}
		},
		[debouncedSearch],
	);

	useEffect(() => {
		void loadPage(0, false);
	}, [loadPage]);

	const loadMore = useCallback(() => {
		if (!isLoading && hasMore) {
			void loadPage(users.length, true);
		}
	}, [isLoading, hasMore, users.length, loadPage]);

	return { users, search, setSearch, isLoading, hasError, hasMore, loadMore };
};
